import { useState, useRef, useEffect, type DragEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Loader2,
  Image as ImageIcon,
  X,
  Copy,
  Check,
  Trash2,
  Upload,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { apiGet, apiPut, apiDelete } from '../lib/api'

interface LibraryImage {
  id: string
  url: string
  filename: string
  altText: string
  tags: string
  sizeBytes: number
  mime: string
  uploadedBy: string
  createdAt: string
}

interface LibraryData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: LibraryImage[]
}

const MAX_SIZE_MB = 10
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function BancoImagenes() {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<LibraryImage | null>(null)
  const [showUpload, setShowUpload] = useState(false)

  // Reset page when search changes
  useEffect(() => {
    setPage(1)
  }, [q])

  const { data, isLoading, isFetching } = useQuery<LibraryData>({
    queryKey: ['admin-image-library', q, page],
    queryFn: () => {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      params.set('page', String(page))
      return apiGet(`/api/admin/image-library?${params.toString()}`)
    },
  })

  return (
    <>
      <Topbar
        title="Banco de imágenes"
        subtitle={data ? `${data.total} imágenes` : 'Cargando…'}
        actions={
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Subir
          </button>
        }
      />

      <div className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, alt o tag…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
            </div>
          )}

          {data && data.data.length === 0 && (
            <div className="grid place-items-center py-16 text-center">
              <ImageIcon className="size-10 text-ink-300" />
              <p className="mt-3 text-sm font-medium text-ink-700">
                {q ? 'No hay resultados' : 'No hay imágenes en el banco'}
              </p>
              <p className="text-xs text-ink-500">
                {q ? 'Prueba con otra búsqueda.' : 'Sube la primera para empezar.'}
              </p>
            </div>
          )}

          {data && data.data.length > 0 && (
            <div className="relative p-4">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {data.data.map((img) => (
                  <button
                    type="button"
                    key={img.id}
                    onClick={() => setSelected(img)}
                    className="group relative aspect-square overflow-hidden rounded-lg border border-ink-200 bg-ink-50 transition-all hover:border-brand-500 hover:shadow-md"
                  >
                    <img
                      src={img.url}
                      alt={img.altText || img.filename}
                      className="size-full object-contain"
                      loading="lazy"
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).src = '/img/sin-imagen.jpg'
                      }}
                    />
                    <div className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-ink-900/80 to-transparent px-2 py-1.5 text-left text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                      {img.filename}
                    </div>
                  </button>
                ))}
              </div>

              {data.pages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-40"
                  >
                    Anterior
                  </button>
                  <span className="text-xs text-ink-600">
                    Página {page} de {data.pages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-40"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            qc.invalidateQueries({ queryKey: ['admin-image-library'] })
          }}
        />
      )}

      {selected && (
        <DetailModal
          image={selected}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => {
            setSelected(updated)
            qc.invalidateQueries({ queryKey: ['admin-image-library'] })
          }}
          onDeleted={() => {
            setSelected(null)
            qc.invalidateQueries({ queryKey: ['admin-image-library'] })
          }}
        />
      )}
    </>
  )
}

// ---------- Upload Modal ----------

interface UploadItem {
  file: File
  status: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
  url?: string
}

function UploadModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void
  onUploaded: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [items, setItems] = useState<UploadItem[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [tags, setTags] = useState('')
  const [running, setRunning] = useState(false)

  function addFiles(files: FileList | File[]) {
    const arr: UploadItem[] = []
    for (const f of Array.from(files)) {
      if (!f.type.startsWith('image/')) continue
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        arr.push({ file: f, status: 'error', error: `>${MAX_SIZE_MB}MB` })
        continue
      }
      arr.push({ file: f, status: 'pending' })
    }
    setItems((prev) => [...prev, ...arr])
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) addFiles(e.target.files)
    e.target.value = ''
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files)
  }

  async function uploadAll() {
    if (running) return
    setRunning(true)
    let anyOk = false
    for (let i = 0; i < items.length; i++) {
      if (items[i].status !== 'pending') continue
      setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, status: 'uploading' } : it)))
      try {
        const fd = new FormData()
        fd.append('file', items[i].file)
        if (tags.trim()) fd.append('tags', tags.trim())
        const r = await fetch('/api/admin/image-library', {
          method: 'POST',
          credentials: 'same-origin',
          body: fd,
        })
        const data = (await r.json().catch(() => ({}))) as {
          ok?: boolean
          data?: { url?: string }
          error?: string
        }
        if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
        anyOk = true
        setItems((prev) =>
          prev.map((it, idx) =>
            idx === i ? { ...it, status: 'done', url: data.data?.url } : it,
          ),
        )
      } catch (e) {
        setItems((prev) =>
          prev.map((it, idx) =>
            idx === i ? { ...it, status: 'error', error: (e as Error).message } : it,
          ),
        )
      }
    }
    setRunning(false)
    if (anyOk) onUploaded()
  }

  const pendingCount = items.filter((it) => it.status === 'pending').length
  const allDone = items.length > 0 && items.every((it) => it.status === 'done' || it.status === 'error')

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/60 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <h2 className="text-sm font-semibold text-ink-900">Subir imágenes</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="p-5">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`grid cursor-pointer place-items-center rounded-lg border-2 border-dashed py-12 text-center transition-colors ${
              dragOver
                ? 'border-brand-500 bg-brand-50'
                : 'border-ink-200 bg-ink-50 hover:border-brand-400 hover:bg-brand-50/30'
            }`}
          >
            <div className="flex flex-col items-center gap-2 px-6 text-ink-600">
              <Upload className="size-7 text-ink-400" />
              <span className="text-sm font-medium text-ink-700">
                Arrastra varias imágenes o haz click
              </span>
              <span className="text-[11px] text-ink-500">
                JPG · PNG · WebP · GIF · AVIF — máx {MAX_SIZE_MB}MB c/u
              </span>
            </div>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            onChange={onPick}
            className="hidden"
          />

          <div className="mt-4">
            <label className="text-xs font-semibold text-ink-700">Tags (opcional, aplica a todas)</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="banner, home, promo"
              disabled={running}
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-ink-50"
            />
          </div>

          {items.length > 0 && (
            <div className="mt-4 max-h-60 space-y-1.5 overflow-y-auto rounded-lg border border-ink-100 bg-ink-50 p-2">
              {items.map((it, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 rounded-md bg-white px-2 py-1.5 text-xs"
                >
                  <span className="truncate text-ink-700">{it.file.name}</span>
                  <span className="shrink-0">
                    {it.status === 'pending' && <span className="text-ink-500">en cola</span>}
                    {it.status === 'uploading' && (
                      <Loader2 className="size-3.5 animate-spin text-brand-600" />
                    )}
                    {it.status === 'done' && (
                      <Check className="size-3.5 text-emerald-600" />
                    )}
                    {it.status === 'error' && (
                      <span className="text-red-600">{it.error || 'error'}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-ink-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
          >
            {allDone ? 'Cerrar' : 'Cancelar'}
          </button>
          <button
            type="button"
            onClick={uploadAll}
            disabled={running || pendingCount === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {running && <Loader2 className="size-3.5 animate-spin" />}
            Subir {pendingCount > 0 ? `(${pendingCount})` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------- Detail Modal ----------

function DetailModal({
  image,
  onClose,
  onUpdated,
  onDeleted,
}: {
  image: LibraryImage
  onClose: () => void
  onUpdated: (img: LibraryImage) => void
  onDeleted: () => void
}) {
  const [altText, setAltText] = useState(image.altText)
  const [tags, setTags] = useState(image.tags)
  const [copied, setCopied] = useState(false)

  // Re-sync if a different image is selected
  useEffect(() => {
    setAltText(image.altText)
    setTags(image.tags)
  }, [image.id])

  const save = useMutation({
    mutationFn: () =>
      apiPut<{ ok: boolean; data: LibraryImage }>(`/api/admin/image-library/${image.id}`, {
        alt_text: altText,
        tags,
      }),
    onSuccess: (r) => {
      onUpdated(r.data)
    },
  })

  const del = useMutation({
    mutationFn: () => apiDelete<{ ok: boolean }>(`/api/admin/image-library/${image.id}`),
    onSuccess: () => onDeleted(),
  })

  function onCopy() {
    const absolute = window.location.origin + image.url
    navigator.clipboard.writeText(absolute).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  function onDelete() {
    if (!confirm(`¿Eliminar imagen "${image.filename}"?\nSe borrará también el archivo.`)) return
    del.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/60 p-4">
      <div className="grid w-full max-w-4xl grid-cols-1 overflow-hidden rounded-2xl bg-white shadow-2xl md:grid-cols-[1fr_320px]">
        <div className="relative grid place-items-center bg-ink-100 p-4">
          <img
            src={image.url}
            alt={image.altText || image.filename}
            className="max-h-[70vh] w-full object-contain"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).src = '/img/sin-imagen.jpg'
            }}
          />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
            <h2 className="truncate text-sm font-semibold text-ink-900">{image.filename}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-4 text-xs">
            <div>
              <label className="font-semibold text-ink-700">URL</label>
              <div className="mt-1 flex gap-1">
                <input
                  readOnly
                  value={image.url}
                  className="flex-1 rounded-md border border-ink-200 bg-ink-50 px-2 py-1.5 font-mono text-[11px] text-ink-700"
                />
                <button
                  type="button"
                  onClick={onCopy}
                  className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50"
                  title="Copiar URL absoluta"
                >
                  {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="font-semibold text-ink-700">Texto alternativo</label>
              <input
                type="text"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Descripción para SEO/accesibilidad"
                className="mt-1 w-full rounded-md border border-ink-200 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            <div>
              <label className="font-semibold text-ink-700">Tags</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="banner, home, promo"
                className="mt-1 w-full rounded-md border border-ink-200 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            <div className="rounded-md bg-ink-50 px-2 py-1.5 text-[11px] text-ink-600">
              <div>Tipo: {image.mime}</div>
              <div>Tamaño: {formatSize(image.sizeBytes)}</div>
              <div>Subida: {new Date(image.createdAt).toLocaleString('es-MX')}</div>
            </div>

            {save.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
                {(save.error as Error).message}
              </div>
            )}
            {del.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
                {(del.error as Error).message}
              </div>
            )}
          </div>

          <div className="flex justify-between gap-2 border-t border-ink-100 px-4 py-3">
            <button
              type="button"
              onClick={onDelete}
              disabled={del.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="size-3.5" /> Eliminar
            </button>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {save.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Guardar cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BancoImagenes
